import React, {useEffect, useState, useCallback, useRef} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchQuiz, submitScore } from "../api";
import { recordQuizResult } from "../lib/progress";
import InteractiveMascot from "../components/InteractiveMascot";

// Sound effects
const CORRECT_SOUND = new Audio("/sounds/correct.mp3");
const WRONG_SOUND = new Audio("/sounds/wrong.mp3");
const COMPLETE_SOUND = new Audio("/sounds/complete.mp3");

// Prevent sounds from playing simultaneously
const playSound = (audio) => {
  audio.currentTime = 0;
  audio.play().catch(() => {}); // Ignore errors if sound is blocked
};

export default function Quiz({user}) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const mascotRef = useRef();
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check subscription status for Class 5 quizzes
  const isGuest = !user || user.id === -1;
  const hasActiveSubscription = user && user.subscription && user.subscription.status === 1;
  const isSubscribed = hasActiveSubscription || (user && user.role >= 2); // Premium or Admin
  const isClass5 = parseInt(classId) === 5;
  
  // If trying to access Class 5 without subscription, redirect
  useEffect(() => {
    if (isClass5 && !isSubscribed) {
      navigate('/subscription-manager');
      return;
    }
  }, [isClass5, isSubscribed, navigate]);

  useEffect(()=>{
    const loadQuiz = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Loading quiz for class:', classId);
        const startTime = Date.now();
        
        // Add a timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Quiz loading timeout - please try again')), 10000) // 10 second timeout
        );
        
        const quizPromise = fetchQuiz(classId);
        
        const questions = await Promise.race([quizPromise, timeoutPromise]);
        
        const loadTime = Date.now() - startTime;
        console.log(`Quiz loaded in ${loadTime}ms, ${questions.length} questions`);
        
        if (!questions || questions.length === 0) {
          throw new Error('No questions available for this class');
        }
        
        setQuestions(questions);
        setLoading(false);
      } catch (error) {
        console.error('Error loading quiz:', error);
        setError(error.message || 'Failed to load quiz');
        setLoading(false);
      }
    };
    
    loadQuiz();
  }, [classId]);

  const choose = useCallback((choiceIdx) => {
    if (showFeedback) return; // Prevent multiple answers
    
    const correct = choiceIdx === questions[index].answer;
    setIsCorrect(correct);
    setShowFeedback(true);
    setAnswers(prev => ({...prev, [index]: choiceIdx}));
    
    // Play appropriate sound
    playSound(correct ? CORRECT_SOUND : WRONG_SOUND);
    
    // Trigger mascot response
    if (mascotRef.current) {
      mascotRef.current.handleQuizResponse(correct);
    }
    
    setTimeout(() => {
      setShowFeedback(false);
      setIndex(i => i + 1);
    }, 1500); // Slightly longer delay for better feedback
  }, [showFeedback, questions, index]);

  function finish() {
    let correct = 0;
    let total = questions.length;
    questions.forEach((q,i)=>{
      if (answers[i] === q.answer) { correct++; }
    });
    // Calculate score as percentage
    const totalScore = Math.round((correct / total) * 100);
    setScore(totalScore);
    setDone(true);

    // Play completion sound
    playSound(COMPLETE_SOUND);

    // Update local progress and achievements
    recordQuizResult(totalScore);

    // Trigger mascot completion celebration
    setTimeout(() => {
      if (mascotRef.current) {
        const percentage = Math.round((correct / total) * 100);
        mascotRef.current.handleQuizCompletion(totalScore, percentage);
      }
    }, 500);

    // Submit score (with error handling)
    submitScore({ 
      userId: user.id === -1 ? 0 : user.id, 
      class: parseInt(classId,10), 
      score: totalScore, 
      correct, 
      total 
    }).then((result) => {
      if (result && result.error) {
        console.warn('Score submission failed, but quiz completed locally:', result.error);
      } else {
        console.log('Score submitted successfully');
      }
    }).catch((error) => {
      console.warn('Score submission failed, but quiz completed locally:', error);
    });
  }

  if (loading) {
    return (
      <>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="card-glow p-8 text-center animate-bounceIn">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-0 border-4 border-secondary border-b-transparent rounded-full animate-spin animation-delay-500"></div>
            </div>
            <h3 className="text-xl font-bold mb-2">Loading Class {classId} Quiz</h3>
            <p className="text-gray-600 dark:text-gray-400">Preparing your math adventure...</p>
          </div>
        </div>
        <InteractiveMascot position="bottom-right" emotion="thinking" />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="card-glow p-8 text-center animate-bounceIn max-w-md">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-bold text-red-600 mb-2">Quiz Loading Failed</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <InteractiveMascot position="bottom-right" emotion="thinking" />
      </>
    );
  }

  if (!questions.length) {
    return (
      <>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="card-glow p-8 text-center animate-bounceIn">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-0 border-4 border-secondary border-b-transparent rounded-full animate-spin animation-delay-500"></div>
            </div>
            <h3 className="text-xl font-bold mb-2">Preparing Your Adventure</h3>
            <p className="text-gray-600 dark:text-gray-400">Loading fun math challenges...</p>
          </div>
        </div>
        <InteractiveMascot position="bottom-right" emotion="thinking" />
      </>
    );
  }

  if (index >= questions.length && !done) {
    return (
      <>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="card-glow p-8 text-center max-w-md w-full animate-bounceIn">
            <div className="text-6xl mb-4 animate-float">🎉</div>
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-shine">
              Quest Complete!
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
              Great job completing all the challenges!
            </p>
            <button 
              onClick={finish} 
              className="btn-primary w-full group transition-all duration-300"
            >
              <span className="inline-flex items-center">
                <span className="mr-2 group-hover:rotate-180 transition-transform duration-500">🏆</span>
                See Your Results
              </span>
            </button>
          </div>
        </div>
        <InteractiveMascot position="bottom-right" emotion="celebration" />
      </>
    );
  }

  if (done) {
    const correctCount = Object.values(answers).filter((a, i) => a === questions[i].answer).length;
    const percentage = Math.round((correctCount / questions.length) * 100);
    
    return (
      <>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="card-glow p-8 text-center max-w-md w-full animate-bounceIn">
            <div className="text-6xl mb-4 animate-float">
              {percentage >= 80 ? "🌟" : percentage >= 60 ? "🎉🏆" : "👏"}
            </div>
            <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-shine">
              {percentage >= 80 ? "Outstanding!" : percentage >= 60 ? "Great Job!" : "Good Try!"}
            </h2>
            <div className="mb-6">
              <p className="text-xl mb-2">
                You scored <span className="font-bold text-primary">{score} points</span>
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center">
                  <span className="text-correct mr-1">✓</span> {correctCount} correct
                </span>
                <span>•</span>
                <span>{percentage}% accuracy</span>
              </div>
            </div>
            <div className="space-y-4">
              <button 
                onClick={()=>navigate('/leaderboard')} 
                className="btn-primary w-full group"
              >
                <span className="inline-flex items-center">
                  <span className="mr-2 group-hover:scale-125 transition-transform">🏆</span>
                  View Leaderboard
                </span>
              </button>
              <button 
                onClick={()=>navigate('/')} 
                className="btn-secondary w-full group"
              >
                <span className="inline-flex items-center">
                  <span className="mr-2 group-hover:rotate-180 transition-transform">↺</span>
                  Try Another Level
                </span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Interactive Mascot */}
        <InteractiveMascot 
          ref={mascotRef}
          position="bottom-right" 
          emotion="celebration"
          onInteraction={(type, data) => console.log('Quiz mascot interaction:', type, data)}
        />
      </>
    );
  }

  const q = questions[index];
  const progress = ((index + 1) / questions.length) * 100;

  return (
    <>
      <div className="max-w-2xl mx-auto px-4">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Question {index + 1} of {questions.length}
            </span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Grade {classId}
            </span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="card-glow p-8">
          <h3 className="text-2xl font-bold mb-8 text-center">{q.question}</h3>
          <div className="grid gap-4">
            {q.choices.map((ch, ci) => {
              let buttonClass = "answer-button ";
              if (showFeedback) {
                if (ci === q.answer) {
                  buttonClass += "correct";
                } else if (ci === answers[index]) {
                  buttonClass += "wrong";
                }
              }
              
              return (
                <button 
                  key={ci} 
                  onClick={() => choose(ci)} 
                  className={buttonClass}
                  disabled={showFeedback}
                >
                  <span className="flex items-center">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-3 text-primary">
                      {String.fromCharCode(65 + ci)}
                    </span>
                    {ch}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Interactive Mascot */}
      <InteractiveMascot 
        ref={mascotRef}
        position="bottom-right" 
        emotion={showFeedback ? (isCorrect ? "clapping" : "thinking") : "idle"}
        onInteraction={(type, data) => console.log('Quiz mascot interaction:', type, data)}
      />
    </>
  );
}